/**
 * Event Bus for Cross-Component Communication
 */

type EventHandler = (data: unknown) => void;

class EventBus {
  private handlers: Map<string, Set<EventHandler>> = new Map();

  /**
   * Subscribe to an event
   */
  on(event: string, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from an event
   */
  off(event: string, handler: EventHandler): void {
    const eventHandlers = this.handlers.get(event);
    if (eventHandlers) {
      eventHandlers.delete(handler);
      if (eventHandlers.size === 0) {
        this.handlers.delete(event);
      }
    }
  }

  /**
   * Emit an event
   */
  emit(event: string, data?: unknown): void {
    const eventHandlers = this.handlers.get(event);
    if (eventHandlers) {
      eventHandlers.forEach((handler) => {
        try {
          handler(data);
        } catch (e) {
          console.error(`[EventBus] Error in handler for event "${event}":`, e);
        }
      });
    }
  }

  /**
   * Subscribe to an event once
   */
  once(event: string, handler: EventHandler): () => void {
    const wrappedHandler = (data: unknown) => {
      this.off(event, wrappedHandler);
      handler(data);
    };
    return this.on(event, wrappedHandler);
  }

  /**
   * Clear all handlers for an event
   */
  clear(event?: string): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }
}

// Predefined event names
export const Events = {
  // App switching
  APP_SWITCH_REQUEST: 'app:switch:request',
  APP_SWITCH_COMPLETE: 'app:switch:complete',

  // Notifications
  NOTIFICATION_RECEIVED: 'notification:received',
  NOTIFICATION_READ: 'notification:read',
  NOTIFICATION_CLICK: 'notification:click',
  UNREAD_COUNT_CHANGED: 'notification:unread:changed',

  // Context
  CONTEXT_SWITCHED: 'context:switched',
  ORGANIZATION_SWITCHED: 'organization:switched',

  // Auth
  USER_LOADED: 'user:loaded',
  USER_LOGOUT: 'user:logout',
  TOKEN_REFRESHED: 'token:refreshed',

  // User presence
  USER_STATUS_CHANGED: 'user:status:changed',
  USER_STATUS_UPDATE: 'user:status:update',

  // Navigation / App context
  NAVIGATION_CONTEXT_UPDATE: 'navigation:context:update',
  PAGE_TITLE_CHANGED: 'page:title:changed',
  BREADCRUMBS_CHANGED: 'navigation:breadcrumbs:changed',

  // Settings
  SETTING_CHANGED: 'setting:changed',
  THEME_CHANGED: 'theme:changed',

  // Connection
  WS_CONNECTED: 'ws:connected',
  WS_DISCONNECTED: 'ws:disconnected',

  // Branding
  BRANDING_CHANGED: 'branding:changed',

  // Search
  SEARCH_QUERY: 'search:query',
  SEARCH_SUBMIT: 'search:submit',
  SEARCH_CLEAR: 'search:clear',

  // Messenger
  MESSENGER_MESSAGE_RECEIVED: 'messenger:message:received',
  MESSENGER_UNREAD_CHANGED: 'messenger:unread:changed',
  MESSENGER_TYPING: 'messenger:typing',
  MESSENGER_PRESENCE: 'messenger:presence',
  MESSENGER_WIDGET_OPENED: 'messenger:widget:opened',
  MESSENGER_WIDGET_CLOSED: 'messenger:widget:closed',
} as const;

// Export singleton instance
export const eventBus = new EventBus();

export default eventBus;
